import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TablePagination
} from '@mui/material';
import { api } from '../context/AuthContext';

const PurchaseHistoryPage = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const res = await api.get('/applications/comm-center/purchases');

      // ✅ FIX: Safely handles both object layouts { data: [] } and raw arrays []
      if (res.data && Array.isArray(res.data.data)) {
        setPurchases(res.data.data);
      } else if (Array.isArray(res.data)) {
        setPurchases(res.data);
      } else {
        setPurchases([]);
      }
    } catch (err) {
      console.error('Failed to fetch purchases:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getCreditTypeLabel = (type) => {
    if (type === 'calling') return 'Call Alerts';
    if (type === 'whatsapp') return 'WhatsApp Alerts';
    return type;
  };

  const getCreditTypeColor = (type, source) => {
    if (source === 'custom_plan') return '#1E3A8A';
    if (type === 'calling') return '#2563EB';
    if (type === 'whatsapp') return '#059669';
    return '#64748B';
  };

  return (
      <Box sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ color: "#1E3A8A", fontWeight: "bold", fontSize: { xs: '1.5rem', md: '2rem' } }}>
                    Purchase History
                  </Typography>

          <Typography variant="body1" color="text.secondary">
            Track all your Call Alert and WhatsApp Alert credit purchases and payments.
          </Typography>
        </Box>

        <Paper
            sx={{
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              overflowX: 'auto'
            }}
        >
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Purchase Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Credit Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Package Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Credits Purchased
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Amount Paid
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Transaction ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Payment Status
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress size={24} sx={{ my: 2 }} />
                      </TableCell>
                    </TableRow>
                ) : purchases.length > 0 ? (
                    purchases
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell>
                                <Typography variant="body2">
                                  {new Date(row.created_at).toLocaleDateString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(row.created_at).toLocaleTimeString()}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1
                                    }}
                                >
                                  <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: getCreditTypeColor(
                                            row.credit_type,
                                            row.source
                                        )
                                      }}
                                  />

                                  <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: getCreditTypeColor(
                                            row.credit_type,
                                            row.source
                                        )
                                      }}
                                  >
                                    {getCreditTypeLabel(row.credit_type)}
                                  </Typography>
                                </Box>

                                {row.source === 'custom_plan' && (
                                    <Chip
                                        label="Custom Plan"
                                        size="small"
                                        sx={{
                                          mt: 0.5,
                                          height: 18,
                                          fontSize: '0.6rem',
                                          bgcolor: '#EFF6FF',
                                          color: '#1E3A8A',
                                          fontWeight: 700
                                        }}
                                    />
                                )}
                              </TableCell>

                              <TableCell>
                                <Typography variant="body2">
                                  {row.package_name}
                                </Typography>
                              </TableCell>

                              <TableCell align="right">
                                <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 700,
                                      color: '#059669'
                                    }}
                                >
                                  +{row.credits_purchased}
                                </Typography>
                              </TableCell>

                              <TableCell align="right">
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700 }}
                                >
                                  ₹{Number(row.amount_paid).toLocaleString()}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      color: 'text.secondary',
                                      fontSize: '0.75rem'
                                    }}
                                >
                                  {row.transaction_id}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Chip
                                    label={row.payment_status}
                                    size="small"
                                    color={
                                      row.payment_status === 'paid'
                                          ? 'success'
                                          : 'warning'
                                    }
                                    sx={{
                                      fontWeight: 600,
                                      textTransform: 'capitalize'
                                    }}
                                />
                              </TableCell>
                            </TableRow>
                        ))
                ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No purchase history found
                        </Typography>
                      </TableCell>
                    </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && purchases.length > 0 && (
              <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={purchases.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
              />
          )}
        </Paper>
      </Box>
  );
};

export default PurchaseHistoryPage;